import { FunctionComponent, useEffect, useMemo, useState } from "react";
import {
    OverviewScope,
    ServiceScoreViewModel
} from "../../models/OverviewDashboard";
import {
    ValueHistoryChartMode,
    ValueHistorySeries
} from "../../models/ValueHistory";
import ValueHistoryService from "../../services/ValueHistoryService";
import useStableStringArray from "../../hooks/useStableStringArray";
import ValueHistoryChart from "./ValueHistoryChart";
import styles from "./EvolutionHistoryPanel.module.css";

interface EvolutionHistoryPanelProps {
    fhirBaseUrl: string;
    measureId: string;
    scope: OverviewScope;
    services: ServiceScoreViewModel[];
    selectedServiceId?: string;
    poleName: string;
}

const EvolutionHistoryPanel: FunctionComponent<EvolutionHistoryPanelProps> = ({
    fhirBaseUrl,
    measureId,
    scope,
    services,
    selectedServiceId,
    poleName
}) => {
    const [mode, setMode] = useState<ValueHistoryChartMode>("line");
    const [history, setHistory] = useState<ValueHistorySeries>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string>();

    const historyService = useMemo(
        () => new ValueHistoryService({ fhirBaseUrl, measureId }),
        [fhirBaseUrl, measureId]
    );

    const selectedService = selectedServiceId
        ? services.find((item) => item.service.id === selectedServiceId)
        : undefined;
    const organizationReferences = useStableStringArray(scope === "pole"
        ? services.map((item) => item.service.reference)
        : selectedService
            ? [selectedService.service.reference]
            : []);
    const hasSelectedService = Boolean(selectedService);
    const contextLabel = scope === "pole"
        ? poleName
        : selectedService?.service.name ?? "Service non sélectionné";

    useEffect(() => {
        let mounted = true;

        if (scope === "services" && !hasSelectedService) {
            setHistory([]);
            setLoading(false);
            setError(undefined);
            return () => {
                mounted = false;
            };
        }

        setLoading(true);
        setError(undefined);

        historyService.load({ organizationReferences, months: 6 })
            .then((result) => {
                if (mounted) setHistory(result);
            })
            .catch((reason) => {
                console.error(reason);
                if (mounted) setError("Impossible de charger l'historique mensuel.");
            })
            .finally(() => {
                if (mounted) setLoading(false);
            });

        return () => {
            mounted = false;
        };
    }, [historyService, organizationReferences, scope, hasSelectedService]);

    return (
        <div className={styles.panel}>
            <header className={styles.header}>
                <div>
                    <h3>Évolution des scores par valeur</h3>
                    <p>{contextLabel} · 6 derniers mois calendaires</p>
                </div>
                <div className={styles.modeSwitch} role="group" aria-label="Type de graphique">
                    <button
                        type="button"
                        className={mode === "line" ? styles.activeMode : undefined}
                        onClick={() => setMode("line")}
                    >
                        Courbe
                    </button>
                    <button
                        type="button"
                        className={mode === "bar" ? styles.activeMode : undefined}
                        onClick={() => setMode("bar")}
                    >
                        Barres
                    </button>
                </div>
            </header>

            {scope === "services" && !selectedService ? (
                <div className={styles.state}>Sélectionnez un service pour consulter son historique.</div>
            ) : loading ? (
                <div className={styles.state}>Chargement de l'historique…</div>
            ) : error ? (
                <div className={styles.error}>{error}</div>
            ) : (
                <ValueHistoryChart data={history} mode={mode} />
            )}
        </div>
    );
};

export default EvolutionHistoryPanel;
